import { IErrorResponse } from '@common/api.models';
import {
  useForm,
  ComponentType,
  Form,
  Scrollbar,
  IconName,
  isNullOrWhiteSpace,
  Accordion,
  IItemResponseBase,
  Button,
  Badge,
  IBreadcrumbItem,
  convertISOStringToLocalDateString,
  convertDateToStringIgnoringTimezone,
} from '@gcl/react-core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { routes } from '@routes/routes.models';
import { AxiosError } from 'axios';
import * as React from 'react';
import {
  useFetchCheckerCompanyOptions,
  useFetchDesignerCompanyOptions,
  useFetchReviewerCompanyOptions,
} from '@company/projectCompany/projectCompany.queries';
import { createDefaultDetailPageTitleFn, DetailPageLayout } from '@app/DetailPageLayout';
import { DetailPageRightMenu } from '@common/detailPageRightMenu/DetailPageRightMenu';
import { getAccessMode } from '@common/detailPage.hooks';
import {
  getMasterScheduleConfirmationActionMessage,
  getMasterScheduleDetail,
  useCanUncancelMasterSchedule,
  useCanLockMasterSchedule,
  useMasterScheduleMode,
} from '@masterSchedule/masterSchedule.hooks';
import { useEffect } from 'react';
import { DetailPageTopBar, IDetailPageTopBarItem } from '@app/DetailPageTopBar';
import { EDetailPageTopBarItemType } from '@app/detailPageTopBar.model';
import { FETCH_QUERIES_NAME } from '@common/constants';
import usePrompt from '@common/prompt.hooks';
import { ECompanyTypeIdConst } from '@company/companyType/companyType.constants';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { useNavigate } from '@routes/routes.hooks';
import { useProjectId, useProjectIsArchived } from '@config/config.hooks';
import { DownloadButton } from '@file/DownloadButton';
import { exportProjectMasterScheduleQrPdfWithIdAsync } from '@masterSchedule/masterSchedule.services';
import { useFetchAllRiskCategories } from '@riskCategory/riskCategory.queries';
import { useFetchAllVoItems } from '@voItem/voItem.queries';
import { isRestrictedAccessFromApiResponse } from '@common/api.services';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { useConfirmationModal } from '@common/confirmation.hooks';
import { EOrganizationTypeId } from '@company/organizationType/organizationType.constants';
import { useFetchTeamsOptionsByCompanyTypeIdOrganizationTypeId } from '@projectTeam/projectTeam.queries';
import { Tab, Tabs } from '@core/Tabs/Tab';
import { Attachments } from '@file/Attachments';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { IProjectFileAttachmentsMasterSchedulesRequest } from '@file/projectFile/projectFile.models';
import { useFetchAttachmentMasterSchedule } from '@file/projectFile/projectFile.queries';
import { IRiskCategory } from '@riskCategory/riskCategory.models';
import { IVoItem } from '@voItem/voItem.models';
import { createMasterScheduleAsync, updateMasterScheduleAsync } from './masterScheduleDetail.services';
import {
  EMasterScheduleConfirmationActionType,
  EMasterScheduleFormKey,
  ICreateT1Request,
  IMasterScheduleDetail,
  IMasterScheduleDetailForm,
  IMasterScheduleFormState,
  IResponsibleUser,
  IUpdateT1Request,
  IUpsertT1ResponsibleUser,
} from './masterScheduleDetail.models';
import { MasterScheduleDetailDesignPackage } from './MasterScheduleDetailDesignPackage';
import { MasterScheduleDetailResponsiblePersonsAndTeams } from './MasterScheduleDetailResponsiblePersonsAndTeams';
import { EDetailPageRightMenuGeneralInfoType } from '../../common/detailPageRightMenu/detailPageRightMenuGeneralInfo.models';
import { useLocalTags } from './tagView/tag.hooks';
import { MasterScheduleDetailDrawingList } from './MasterScheduleDetailDrawingList';
import {
  useCancelMasterSchedule,
  useUncancelMasterSchedule,
  useLockMasterSchedule,
  useUnlockMasterSchedule,
} from './masterScheduleDetail.queries';

interface IMasterScheduleDetailProps {}

export const MasterScheduleDetailPage: React.FunctionComponent<IMasterScheduleDetailProps> = () => {
  const { navigate, generateRoutePath, projectNo } = useNavigate();
  const projectId = useProjectId();
  const isProjectArchived = useProjectIsArchived();

  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });
  const { setValue, formState, reset, getValues, setError } = useFormMethods;
  const { id } = getAccessMode();
  const {
    isInitialLoading: isLoadingGetMasterSchedule,
    tags,
    masterSchedule,
    error: errorGetMasterSchedule,
    isFetching,
  } = getMasterScheduleDetail(projectId, id, !formState?.isDirty);
  const mode = useMasterScheduleMode(masterSchedule, id);
  const canUncancel = useCanUncancelMasterSchedule(masterSchedule);
  const canLock = useCanLockMasterSchedule(masterSchedule);

  const { localTags, onRemove, onAdd } = useLocalTags(tags || []);

  const getDefaultFormValues = (
    masterScheduleDetailLocal: IMasterScheduleDetail | undefined,
    isFetchingLocal: boolean,
  ) => {
    if (isFetchingLocal && useFormMethods.formState.defaultValues) return useFormMethods.formState.defaultValues;
    const convertUser = (user: IResponsibleUser) => {
      return {
        id: user.projectUserProfileId,
        projectId,
        projectMasterScheduleId: id,
        projectUserProfileId: user.projectUserProfileId,
        displayName: user.displayName,
      };
    };

    const formDefaultValues: IMasterScheduleFormState = {
      ...masterScheduleDetailLocal,
      [EMasterScheduleFormKey.REF_NO]: masterScheduleDetailLocal?.refNo ?? null,
      [EMasterScheduleFormKey.DESIGN_PACKAGE]: masterScheduleDetailLocal?.designPackage ?? null,
      [EMasterScheduleFormKey.RISK_CATEGORY]: masterScheduleDetailLocal?.riskCategory ?? null,
      [EMasterScheduleFormKey.VO_ITEM]: masterScheduleDetailLocal?.voItem ?? null,
      [EMasterScheduleFormKey.REQUIRED_WORK_PROCEDURE_DRAWINGS]:
        masterScheduleDetailLocal?.requiredWorkProcedureDrawings ?? false,
      [EMasterScheduleFormKey.PROCUREMENT_DATE]: masterScheduleDetailLocal?.procurementDate
        ? convertISOStringToLocalDateString(masterScheduleDetailLocal?.procurementDate ?? '')
        : null,
      [EMasterScheduleFormKey.START_CONSTRUCTION_DATE]: masterScheduleDetailLocal?.startConstructionDate
        ? convertISOStringToLocalDateString(masterScheduleDetailLocal?.startConstructionDate ?? '')
        : null,
      [EMasterScheduleFormKey.REMOVAL_DATE]: masterScheduleDetailLocal?.removalDate
        ? convertISOStringToLocalDateString(masterScheduleDetailLocal?.removalDate ?? '')
        : null,
      [EMasterScheduleFormKey.REMARKS]: masterScheduleDetailLocal?.remarks ?? null,
      [EMasterScheduleFormKey.HAS_NO_DESIGNER]: masterScheduleDetailLocal?.hasNoDesigner ?? false,
      [EMasterScheduleFormKey.REMARK_FOR_NO_DESIGNER]: masterScheduleDetailLocal?.remarkForNoDesigner ?? null,
      [EMasterScheduleFormKey.RESPONSIBLE_DESIGNER]:
        masterScheduleDetailLocal?.responsibleDesigner?.companyTypeId !== ECompanyTypeIdConst.PLACEHOLDER
          ? masterScheduleDetailLocal?.responsibleDesigner ?? null
          : null,
      [EMasterScheduleFormKey.RESPONSIBLE_CHECKER]:
        masterSchedule?.responsibleChecker?.companyTypeId !== ECompanyTypeIdConst.PLACEHOLDER
          ? masterScheduleDetailLocal?.responsibleChecker ?? null
          : null,
      [EMasterScheduleFormKey.RESPONSIBLE_REVIEWER]: masterScheduleDetailLocal?.responsibleReviewer ?? null,

      [EMasterScheduleFormKey.FALSEWORK]: masterScheduleDetailLocal?.isFalsework ?? false,

      [EMasterScheduleFormKey.RESPONSIBLE_SRP]: (masterScheduleDetailLocal?.responsibleSrp || []).map((srp) => {
        return convertUser(srp);
      }) as any,
      [EMasterScheduleFormKey.RESPONSIBLE_TWC]: (masterScheduleDetailLocal?.responsibleTwc || []).map((twc) => {
        return convertUser(twc);
      }) as any,
      [EMasterScheduleFormKey.RESPONSIBLE_TWCM]: (masterScheduleDetailLocal?.responsibleTwcm || []).map((twcm) => {
        return convertUser(twcm);
      }) as any,
      [EMasterScheduleFormKey.RESPONSIBLE_DESIGNER_TEAM]: (
        masterScheduleDetailLocal?.responsibleDesignerTeams ?? []
      ).map((team) => {
        return { ...team, id: team.projectTeamId, rowVersion: undefined };
      }),
      [EMasterScheduleFormKey.RESPONSIBLE_CHECKER_TEAM]: (masterScheduleDetailLocal?.responsibleCheckerTeams ?? []).map(
        (team) => {
          return { ...team, id: team.projectTeamId, rowVersion: undefined };
        },
      ),
      [EMasterScheduleFormKey.RESPONSIBLE_REVIEWER_TEAM]: (
        masterScheduleDetailLocal?.responsibleReviewerTeams ?? []
      ).map((team) => {
        return { ...team, id: team.projectTeamId, rowVersion: undefined };
      }),
      [EMasterScheduleFormKey.RESPONSIBLE_SITE_OPERATION_TEAM]: (
        masterScheduleDetailLocal?.responsibleSiteOperationTeams ?? []
      ).map((team) => {
        return { ...team, id: team.projectTeamId, rowVersion: undefined };
      }),
      [EMasterScheduleFormKey.SUPPLEMENTARY_FILES]: [],
    };
    return formDefaultValues;
  };

  const { data: dataSiteOperationTeamOptions, isLoading: isLoadingSiteOperationTeams } =
    useFetchTeamsOptionsByCompanyTypeIdOrganizationTypeId({
      shouldConvertOptions: false,
    })(
      { projectId, companyTypeId: ECompanyTypeIdConst.SITE_OPERATION, organizationTypeId: EOrganizationTypeId.SITE },
      !formState?.isDirty,
    );
  const { data: dataDesignerCompanyOptions, isLoading: isLoadingTeamsDesigner } = useFetchDesignerCompanyOptions({
    shouldConvertOptions: false,
  })({ projectId }, !formState?.isDirty);
  const { data: dataCheckerCompanyOptions, isLoading: isLoadingTeamsChecker } = useFetchCheckerCompanyOptions({
    shouldConvertOptions: false,
  })({ projectId }, !formState?.isDirty);
  const { data: dataReviewCompanyOptions, isLoading: isLoadingTeamsReviewer } = useFetchReviewerCompanyOptions({
    shouldConvertOptions: false,
  })({ projectId }, !formState?.isDirty);

  const { data: dataRiskCategories, isLoading: isLoadingRiskCategories } = useFetchAllRiskCategories({
    isDirty: formState?.isDirty,
  })();
  const { data: dataVoItems, isLoading: isLoadingVoItems } = useFetchAllVoItems({ isDirty: formState?.isDirty })();

  const queryClient = useQueryClient();

  useEffect(() => {
    if ((useFormMethods.getValues(EMasterScheduleFormKey.RESPONSIBLE_SITE_OPERATION_TEAM)?.length ?? 0) > 0) return;
    const defaultOptions = (dataSiteOperationTeamOptions ?? []).filter((o: any) => o.isDefault === true);
    useFormMethods.setValue(EMasterScheduleFormKey.RESPONSIBLE_SITE_OPERATION_TEAM, defaultOptions);
  }, [JSON.stringify(dataSiteOperationTeamOptions)]);

  // #region update master schedule
  const {
    mutate: mutateUpdateMasterSchedule,
    isLoading: isLoadingUpdateMasterSchedule,
    isError: isErrorUpdateMasterSchedule,
    error: errorUpdateMasterSchedule,
    data: dataUpdateMasterSchedule,
    isSuccess: isSuccessUpdateMasterSchedule,
    reset: resetUpdateMasterSchedule,
  } = useMutation<
    IItemResponseBase<IMasterScheduleDetail>,
    AxiosError<IErrorResponse, any>,
    { id: number; body: IUpdateT1Request },
    any
  >(updateMasterScheduleAsync);

  const {
    mutate: mutateCancelMasterSchedule,
    isLoading: isLoadingCancelMasterSchedule,
    isSuccess: isSuccessCancelMasterSchedule,
    isError: isErrorCancelMasterSchedule,
    error: errorCancelMasterSchedule,
    data: dataCancelMasterSchedule,
    reset: resetCancelMasterSchedule,
  } = useCancelMasterSchedule();

  const {
    mutate: mutateUncancelMasterSchedule,
    isLoading: isLoadingUncancelMasterSchedule,
    isSuccess: isSuccessUncancelMasterSchedule,
    isError: isErrorUncancelMasterSchedule,
    error: errorUncancelMasterSchedule,
    data: dataUncancelMasterSchedule,
    reset: resetUncancelMasterSchedule,
  } = useUncancelMasterSchedule();

  const {
    mutate: mutateLockMasterSchedule,
    isLoading: isLoadingLockMasterSchedule,
    isSuccess: isSuccessLockMasterSchedule,
    isError: isErrorLockMasterSchedule,
    error: errorLockMasterSchedule,
    data: dataLockMasterSchedule,
    reset: resetLockMasterSchedule,
  } = useLockMasterSchedule();

  const {
    mutate: mutateUnlockMasterSchedule,
    isLoading: isLoadingUnlockMasterSchedule,
    isSuccess: isSuccessUnlockMasterSchedule,
    isError: isErrorUnlockMasterSchedule,
    error: errorUnlockMasterSchedule,
    data: dataUnlockMasterSchedule,
    reset: resetUnlockMasterSchedule,
  } = useUnlockMasterSchedule();
  // #endregion

  // #region create master schedule
  const {
    mutate: mutateCreateMasterSchedule,
    isLoading: isLoadingCreateMasterSchedule,
    isError: isErrorCreateMasterSchedule,
    data: dataCreateMasterSchedule,
    error: errorCreateMasterSchedule,
    isSuccess: isSuccessCreateMasterSchedule,
    reset: resetCreateMasterSchedule,
  } = useMutation<IItemResponseBase<IMasterScheduleDetail>, AxiosError<IErrorResponse, any>, ICreateT1Request, any>(
    createMasterScheduleAsync,
  );

  // #endregion

  const [confirmationActionType, setConfirmationActionType] = React.useState<EMasterScheduleConfirmationActionType>();

  const { renderModal: renderConfirmationPortal, openPortal: openConfirmationPortal } = useConfirmationModal({
    content: getMasterScheduleConfirmationActionMessage(confirmationActionType),
    onConfirm: () => {
      switch (confirmationActionType) {
        case EMasterScheduleConfirmationActionType.CANCEL:
          mutateCancelMasterSchedule({ projectId, id: id ?? 0 });
          break;
        case EMasterScheduleConfirmationActionType.UNCANCEL:
          mutateUncancelMasterSchedule({ projectId, id: id ?? 0 });
          break;
        case EMasterScheduleConfirmationActionType.LOCK:
          mutateLockMasterSchedule({ projectId, id: id ?? 0 });
          break;
        case EMasterScheduleConfirmationActionType.UNLOCK:
          mutateUnlockMasterSchedule({ projectId, id: id ?? 0 });
          break;
        default:
          break;
      }
    },
  });

  const handleSubmit = (data: any) => {
    const request = {
      ...(data as IMasterScheduleFormState),
      projectId,
      [EMasterScheduleFormKey.PROCUREMENT_DATE]: data[EMasterScheduleFormKey.PROCUREMENT_DATE]
        ? convertDateToStringIgnoringTimezone(new Date(data[EMasterScheduleFormKey.PROCUREMENT_DATE]))
        : null,
      [EMasterScheduleFormKey.START_CONSTRUCTION_DATE]: data[EMasterScheduleFormKey.START_CONSTRUCTION_DATE]
        ? convertDateToStringIgnoringTimezone(new Date(data[EMasterScheduleFormKey.START_CONSTRUCTION_DATE]))
        : null,
      [EMasterScheduleFormKey.REMOVAL_DATE]: data[EMasterScheduleFormKey.REMOVAL_DATE]
        ? convertDateToStringIgnoringTimezone(new Date(data[EMasterScheduleFormKey.REMOVAL_DATE]))
        : null,
    };

    const masterScheduleDetailFormData = data as IMasterScheduleDetailForm;

    if (mode === 'Create') {
      const responsibleSrp = !masterScheduleDetailFormData.responsibleSrp
        ? []
        : masterScheduleDetailFormData.responsibleSrp.map(({ id: srpProjectUserProfileId, ...rest }) => {
            return { ...rest, projectUserProfileId: srpProjectUserProfileId };
          });
      const responsibleTwc = !masterScheduleDetailFormData.responsibleTwc
        ? []
        : masterScheduleDetailFormData.responsibleTwc.map(({ id: twcProjectUserProfileId, ...rest }) => {
            return { ...rest, projectUserProfileId: twcProjectUserProfileId };
          });
      const responsibleTwcm = !masterScheduleDetailFormData.responsibleTwcm
        ? []
        : masterScheduleDetailFormData.responsibleTwcm.map(({ id: twcmProjectUserProfileId, ...rest }) => {
            return { ...rest, projectUserProfileId: twcmProjectUserProfileId };
          });
      const responsibleDesignerTeams = !masterScheduleDetailFormData.responsibleDesignerTeams
        ? []
        : masterScheduleDetailFormData.responsibleDesignerTeams.map(({ id: designerTeamId, ...rest }) => {
            return { ...rest, projectTeamId: designerTeamId };
          });
      const responsibleCheckerTeams = !masterScheduleDetailFormData.responsibleCheckerTeams
        ? []
        : masterScheduleDetailFormData.responsibleCheckerTeams.map(({ id: checkerTeamId, ...rest }) => {
            return { ...rest, projectTeamId: checkerTeamId };
          });
      const responsibleReviewerTeams = !masterScheduleDetailFormData.responsibleReviewerTeams
        ? []
        : masterScheduleDetailFormData.responsibleReviewerTeams.map(({ id: reviewerTeamId, ...rest }) => {
            return { ...rest, projectTeamId: reviewerTeamId };
          });
      const responsibleSiteOperationTeams = !masterScheduleDetailFormData.responsibleSiteOperationTeams
        ? []
        : masterScheduleDetailFormData.responsibleSiteOperationTeams.map(({ id: siteOperationTeamId, ...rest }) => {
            return { ...rest, projectTeamId: siteOperationTeamId };
          });
      mutateCreateMasterSchedule({
        ...request,
        responsibleSrp,
        responsibleTwc,
        responsibleTwcm,
        responsibleDesignerTeams,
        responsibleCheckerTeams,
        responsibleReviewerTeams,
        responsibleSiteOperationTeams,
        tags: localTags?.map((t) => {
          return { ...t, tagTypeId: t.tagTypeId };
        }),
      } as ICreateT1Request);
    }
    if (mode === 'Update') {
      const responsibleSiteOperationTeams = (masterScheduleDetailFormData.responsibleSiteOperationTeams ?? []).map(
        ({ id: projectTeamId, ...rest }) => {
          const teamObj = masterSchedule?.responsibleSiteOperationTeams?.find((t) => t.projectTeamId === projectTeamId);
          if (teamObj) return { ...rest, id: teamObj.id, rowVersion: teamObj.rowVersion };
          return { ...rest, projectTeamId };
        },
      );
      const responsibleDesignerTeams = (masterScheduleDetailFormData.responsibleDesignerTeams ?? []).map(
        ({ id: projectTeamId, ...rest }) => {
          const teamObj = masterSchedule?.responsibleDesignerTeams?.find((t) => t.projectTeamId === projectTeamId);
          if (teamObj) return { ...rest, id: teamObj.id, projectTeamId, rowVersion: teamObj.rowVersion };
          return { ...rest, projectTeamId };
        },
      );
      const responsibleCheckerTeams = (masterScheduleDetailFormData.responsibleCheckerTeams ?? []).map(
        ({ id: projectTeamId, ...rest }) => {
          const teamObj = masterSchedule?.responsibleCheckerTeams?.find((t) => t.projectTeamId === projectTeamId);
          if (teamObj) return { ...rest, id: teamObj.id, projectTeamId, rowVersion: teamObj.rowVersion };
          return { ...rest, projectTeamId };
        },
      );
      const responsibleReviewerTeams = (masterScheduleDetailFormData.responsibleReviewerTeams ?? []).map(
        ({ id: projectTeamId, ...rest }) => {
          const teamObj = masterSchedule?.responsibleReviewerTeams?.find((t) => t.projectTeamId === projectTeamId);
          if (teamObj) return { ...rest, id: teamObj.id, projectTeamId, rowVersion: teamObj.rowVersion };
          return { ...rest, projectTeamId };
        },
      );
      const responsibleSrp = !masterScheduleDetailFormData.responsibleSrp
        ? []
        : masterScheduleDetailFormData.responsibleSrp.map(({ id: srpProjectUserProfileId, ...rest }) => {
            const srpObj: IUpsertT1ResponsibleUser | undefined = masterSchedule?.responsibleSrp?.find(
              (s) => s.projectUserProfileId === srpProjectUserProfileId,
            );
            if (srpObj)
              return {
                ...rest,
                id: srpObj.id,
                rowVersion: srpObj.rowVersion,
                projectUserProfileId: srpProjectUserProfileId,
              } as IUpsertT1ResponsibleUser;
            return { ...rest, projectUserProfileId: srpProjectUserProfileId } as IUpsertT1ResponsibleUser;
          });
      const responsibleTwc = !masterScheduleDetailFormData.responsibleTwc
        ? []
        : masterScheduleDetailFormData.responsibleTwc.map(({ id: twcProjectUserProfileId, ...rest }) => {
            const twcObj: IUpsertT1ResponsibleUser | undefined = masterSchedule?.responsibleTwc?.find(
              (s) => s.projectUserProfileId === twcProjectUserProfileId,
            );
            if (twcObj)
              return {
                ...rest,
                id: twcObj.id,
                rowVersion: twcObj.rowVersion,
                projectUserProfileId: twcProjectUserProfileId,
              } as IUpsertT1ResponsibleUser;
            return { ...rest, projectUserProfileId: twcProjectUserProfileId } as IUpsertT1ResponsibleUser;
          });
      const responsibleTwcm = !masterScheduleDetailFormData.responsibleTwcm
        ? []
        : masterScheduleDetailFormData.responsibleTwcm.map(({ id: twcmProjectUserProfileId, ...rest }) => {
            const twcmObj: IUpsertT1ResponsibleUser | undefined = masterSchedule?.responsibleTwcm?.find(
              (s) => s.projectUserProfileId === twcmProjectUserProfileId,
            );
            if (twcmObj)
              return {
                ...rest,
                id: twcmObj.id,
                rowVersion: twcmObj.rowVersion,
                projectUserProfileId: twcmProjectUserProfileId,
              } as IUpsertT1ResponsibleUser;
            return { ...rest, projectUserProfileId: twcmProjectUserProfileId } as IUpsertT1ResponsibleUser;
          });
      mutateUpdateMasterSchedule({
        id: id as number,
        body: {
          ...request,
          responsibleSrp,
          responsibleTwc,
          responsibleTwcm,
          responsibleSiteOperationTeams,
          responsibleDesignerTeams,
          responsibleCheckerTeams,
          responsibleReviewerTeams,
          tags: localTags?.map((t) => {
            return { ...t, tagTypeId: t.tagTypeId };
          }),
          rowVersion: masterSchedule?.rowVersion,
        } as IUpdateT1Request,
      });
    }
  };

  const breadcrumbItems: IBreadcrumbItem[] = [
    {
      text: `${projectNo} - Home`,
      prefixIconName: 'House',
      onClick: () => {
        navigate(routes.t1.path);
      },
    },
    {
      text: 'Search T1',
      prefixIconName: 'Search' as IconName,
      onClick: () => {
        navigate(generateRoutePath(routes.t1.path));
      },
    },
    {
      prefixIconName: 'JournalText' as IconName,
      text: `${mode} T1`,
      active: true,
    },
  ];

  const topBarLeftItems: (IDetailPageTopBarItem | React.ReactElement)[] = React.useMemo(() => {
    const items: (IDetailPageTopBarItem | React.ReactElement)[] = [
      ...(mode !== 'View'
        ? [
            {
              type: EDetailPageTopBarItemType.loadingButton,
              componentType: ComponentType.Primary,
              onClick: () => {
                useFormMethods.handleSubmit(handleSubmit)();
              },
              isLoading: isLoadingCreateMasterSchedule || isLoadingUpdateMasterSchedule,
              isError: isErrorCreateMasterSchedule || isErrorUpdateMasterSchedule,
              isSuccess: isSuccessCreateMasterSchedule || isSuccessUpdateMasterSchedule,
              title: 'Save',
              showNoneInReadOnly: true,
              buttonIconName: 'FolderCheck' as IconName,
            },
          ]
        : []),
      ...(masterSchedule?.canCancel === true && !masterSchedule.isLocked && !masterSchedule.isDeleted
        ? [
            {
              type: EDetailPageTopBarItemType.loadingButton,
              componentType: ComponentType.Danger,
              onClick: (e: any) => {
                setConfirmationActionType(EMasterScheduleConfirmationActionType.CANCEL);
                openConfirmationPortal(e);
              },
              isLoading: isLoadingCancelMasterSchedule,
              isError: isErrorCancelMasterSchedule,
              isSuccess: isSuccessCancelMasterSchedule,
              title: 'Cancel',
              showNoneInReadOnly: true,
              buttonIconName: 'FileExcel' as IconName,
            },
          ]
        : []),
      ...(canUncancel
        ? [
            {
              type: EDetailPageTopBarItemType.loadingButton,
              componentType: ComponentType.Danger,
              onClick: (e: any) => {
                setConfirmationActionType(EMasterScheduleConfirmationActionType.UNCANCEL);
                openConfirmationPortal(e);
              },
              isLoading: isLoadingUncancelMasterSchedule,
              isError: isErrorUncancelMasterSchedule,
              isSuccess: isSuccessUncancelMasterSchedule,
              title: 'Uncancel',
              showNoneInReadOnly: true,
              buttonIconName: 'FileExcel' as IconName,
            },
          ]
        : []),
      ...(canLock && masterSchedule?.id && !masterSchedule?.isLocked && !masterSchedule?.isDeleted && !canUncancel
        ? [
            {
              type: EDetailPageTopBarItemType.loadingButton,
              componentType: ComponentType.Danger,
              onClick: (e: any) => {
                setConfirmationActionType(EMasterScheduleConfirmationActionType.LOCK);
                openConfirmationPortal(e);
              },
              isLoading: isLoadingLockMasterSchedule,
              isError: isErrorLockMasterSchedule,
              isSuccess: isSuccessLockMasterSchedule,
              title: 'Lock',
              showNoneInReadOnly: true,
              buttonIconName: 'FileExcel' as IconName,
            },
          ]
        : []),
      ...(canLock && masterSchedule?.isLocked && !masterSchedule?.isDeleted
        ? [
            {
              type: EDetailPageTopBarItemType.loadingButton,
              componentType: ComponentType.Danger,
              onClick: (e: any) => {
                setConfirmationActionType(EMasterScheduleConfirmationActionType.UNLOCK);
                openConfirmationPortal(e);
              },
              isLoading: isLoadingUnlockMasterSchedule,
              isError: isErrorUnlockMasterSchedule,
              isSuccess: isSuccessUnlockMasterSchedule,
              title: 'Unlock',
              showNoneInReadOnly: true,
              buttonIconName: 'FileExcel' as IconName,
            },
          ]
        : []),

      // {
      //   title: 'Print QR',
      //   onClick: () => {},
      //   componentType: ComponentType.Secondary,
      //   type: EDetailPageTopBarItemType.button,
      //   buttonIconName: 'QrCodeScan',
      // },
      ...(id
        ? [
            <DownloadButton
              key="printQrPdf"
              exportFunction={exportProjectMasterScheduleQrPdfWithIdAsync}
              defaultExportProps={{
                id: id ?? -1,
                projectId,
              }}
              buttonRender={(exportFile) => (
                <Button prefixIconName="ArrowBarDown" type={ComponentType.Secondary} onClick={() => exportFile()}>
                  Print QR Code
                </Button>
              )}
            />,
          ]
        : []),
    ];

    return items;
  }, [
    id,
    JSON.stringify(localTags),
    JSON.stringify(mode),
    JSON.stringify(masterSchedule),
    isLoadingCreateMasterSchedule,
    isLoadingUpdateMasterSchedule,
    isLoadingCancelMasterSchedule,
    isLoadingUncancelMasterSchedule,
    isLoadingLockMasterSchedule,
    isLoadingUnlockMasterSchedule,
    isErrorCreateMasterSchedule,
    isErrorUpdateMasterSchedule,
    isErrorCancelMasterSchedule,
    isErrorUncancelMasterSchedule,
    isErrorLockMasterSchedule,
    isErrorUnlockMasterSchedule,
    isSuccessCreateMasterSchedule,
    isSuccessUpdateMasterSchedule,
    isSuccessCancelMasterSchedule,
    isSuccessUncancelMasterSchedule,
    isSuccessLockMasterSchedule,
    isSuccessUnlockMasterSchedule,
    canUncancel,
  ]);

  const topBarRightItems: IDetailPageTopBarItem[] = React.useMemo<IDetailPageTopBarItem[]>(() => {
    return [
      {
        type: EDetailPageTopBarItemType.tagView,
        isLoading: mode === 'Create' ? false : isLoadingGetMasterSchedule,
        tags: localTags,
        error: mode === 'Create' ? undefined : errorGetMasterSchedule,
        onRemoveTag: onRemove,
        onAddTag: onAdd,
        disable: mode === 'View',
      },
    ];
  }, [isLoadingGetMasterSchedule, tags, localTags, errorGetMasterSchedule, mode]);

  const createAccordion = (header: string, body: React.ReactElement) => {
    return (
      <Accordion
        allowClickHeaderToExpand
        className="gcl-gems-t1-detail-page-accordion"
        bodyClassName="gcl-gems-t1-detail-page-accordion-body"
        content={{
          id: `gcl-gems-t1-detail-page-accordion ${header}`,
          onRenderBody: () => {
            return <div className="gcl-gems-t1-detail-page-grid-item">{body}</div>;
          },
          onRenderHeader: header,
          defaultIsExpanded: true,
        }}
      />
    );
  };

  const isRestricted = React.useMemo(() => {
    return isRestrictedAccessFromApiResponse([errorGetMasterSchedule]);
  }, [JSON.stringify(errorGetMasterSchedule)]);

  const isReadOnly = mode === 'View' || isProjectArchived;

  return (
    <Scrollbar>
      <ApiErrorDisplay
        setFormError={setError}
        errors={[
          { error: errorGetMasterSchedule, genericError: 'Get T1 failed' },
          { error: errorCreateMasterSchedule, genericError: 'Create T1 failed' },
          { error: errorUpdateMasterSchedule, genericError: 'Update T1 failed' },
          { error: errorCancelMasterSchedule, genericError: 'Cancel T1 failed' },
          { error: errorUncancelMasterSchedule, genericError: 'Unancel T1 failed' },
          { error: errorLockMasterSchedule, genericError: 'Lock T1 failed' },
          { error: errorUnlockMasterSchedule, genericError: 'Unlock T1 failed' },
        ]}
      />
      <ApiSuccessToast
        successes={[
          { isSuccess: isSuccessCreateMasterSchedule, message: 'Create T1 success', reset: resetCreateMasterSchedule },
          { isSuccess: isSuccessUpdateMasterSchedule, message: 'Update T1 success', reset: resetUpdateMasterSchedule },
          { isSuccess: isSuccessCancelMasterSchedule, message: 'Cancel T1 success', reset: resetCancelMasterSchedule },
          {
            isSuccess: isSuccessUncancelMasterSchedule,
            message: 'Uncancel T1 success',
            reset: resetUncancelMasterSchedule,
          },
          { isSuccess: isSuccessLockMasterSchedule, message: 'Lock T1 success', reset: resetLockMasterSchedule },
          {
            isSuccess: isSuccessUnlockMasterSchedule,
            message: 'Unlock T1 success',
            reset: resetUnlockMasterSchedule,
          },
        ]}
        callbackAfterToasted={() => {
          if (isSuccessCreateMasterSchedule) {
            reset(useFormMethods.getValues());
            const newId = dataCreateMasterSchedule?.item?.id;
            queryClient.invalidateQueries([projectId, FETCH_QUERIES_NAME.MASTER_SCHEDULE_DETAIL, newId]);
            const path = generateRoutePath(routes.t1.view.path, { id: newId?.toString() });
            setTimeout(() => {
              navigate(path);
            }, 0);
            return;
          }

          queryClient.invalidateQueries([projectId, FETCH_QUERIES_NAME.MASTER_SCHEDULE_DETAIL, id]);
          reset(useFormMethods.getValues());
        }}
      />
      <DetailPageLayout
        errorGetDetail={errorGetMasterSchedule}
        isDrawer={mode !== 'Create'}
        isRestricted={isRestricted}
        data={mode === 'Create' ? 'Place Holder' : masterSchedule}
        isLoading={
          isLoadingTeamsDesigner ||
          isLoadingTeamsChecker ||
          isLoadingTeamsReviewer ||
          isLoadingSiteOperationTeams ||
          isLoadingGetMasterSchedule ||
          isLoadingRiskCategories ||
          isLoadingVoItems
        }
        isRefetching={isFetching}
        rightMenu={(setIsDrawerOpen) => (
          <DetailPageRightMenu
            masterSchedule={masterSchedule}
            setIsDrawerOpen={setIsDrawerOpen}
            type={EDetailPageRightMenuGeneralInfoType.T1}
          />
        )}
        title={createDefaultDetailPageTitleFn(
          <>
            {`T1 Package${
              mode !== 'Create' && !isNullOrWhiteSpace(masterSchedule?.refNo) ? ` - ${masterSchedule?.refNo}` : ''
            }`}
            {masterSchedule?.isCancelled && (
              <Badge pill type={ComponentType.Secondary} className="gcl-ml-sm gcl-fb-md gcl-fc-greyscale-100 ">
                Cancelled
              </Badge>
            )}
            {masterSchedule?.isDeleted && (
              <Badge pill type={ComponentType.Secondary} className="gcl-ml-sm gcl-fb-md gcl-fc-greyscale-100 ">
                Deleted
              </Badge>
            )}
            {masterSchedule?.isLocked && (
              <Badge pill type={ComponentType.Secondary} className="gcl-ml-sm gcl-fb-md gcl-fc-greyscale-100 ">
                Locked
              </Badge>
            )}
          </>,
        )}
        breadcrumb={breadcrumbItems}
        render={() => {
          return (
            <Form
              defaultValues={{
                callback: () => {
                  const state = getDefaultFormValues(masterSchedule, isFetching);
                  return state;
                },
                deps: [masterSchedule, isFetching],
              }}
              enableUnsavedChangesPrompt
              unsavedChangesPromptProps={{
                usePrompt,
              }}
              key={masterSchedule?.id}
              useFormMethods={useFormMethods}
              render={(formFieldMethods) => {
                return (
                  <Tabs>
                    <Tab headerText="Details">
                      <DetailPageTopBar leftItems={topBarLeftItems} rightItems={topBarRightItems} />

                      <div className="gcl-gems-t1-detail-page-grid-container">
                        {createAccordion(
                          'Design Package',
                          <MasterScheduleDetailDesignPackage
                            formFieldMethods={formFieldMethods}
                            isReadOnly={isReadOnly}
                            dataRiskCategories={(dataRiskCategories ?? []) as IRiskCategory[]}
                            dataVoItems={(dataVoItems ?? []) as IVoItem[]}
                          />,
                        )}
                        {createAccordion(
                          'Responsible Persons and Teams',
                          <MasterScheduleDetailResponsiblePersonsAndTeams
                            mode={mode}
                            formFieldMethods={formFieldMethods}
                            checkerCompanyOptions={dataCheckerCompanyOptions || []}
                            designerCompanyOptions={dataDesignerCompanyOptions || []}
                            reviewCompanyOptions={dataReviewCompanyOptions || []}
                            projectId={projectId}
                            siteOperationTeamOptions={dataSiteOperationTeamOptions || []}
                            masterScheduleId={id}
                            isReadOnly={isReadOnly}
                          />,
                        )}
                        {/* {createAccordion('UDF', <MasterScheduleDetailUdf />)} */}
                        {createAccordion('Drawings', <MasterScheduleDetailDrawingList masterScheduleId={id ?? 0} />)}
                      </div>
                    </Tab>
                    <Tab headerText="Supplementary Info">
                      <Attachments<IProjectFileAttachmentsMasterSchedulesRequest>
                        id={`supplementary-info-${EProjectFileType.T1_SUPPLEMENTARY}`}
                        mode={mode}
                        hasUploadRight={mode !== 'View'}
                        fileType={EProjectFileType.T1_SUPPLEMENTARY}
                        uploadFileRequestParam={{ projectMasterScheduleId: id }}
                        useFetchFiles={useFetchAttachmentMasterSchedule}
                        useFetchFilesProps={{
                          projectMasterScheduleId: id ?? 0,
                          projectId,
                          fileType: EProjectFileType.T1_SUPPLEMENTARY,
                        }}
                        formFieldMethods={formFieldMethods}
                        formKeyToUpdateInCreateMode={EMasterScheduleFormKey.SUPPLEMENTARY_FILES}
                      />
                    </Tab>
                  </Tabs>
                );
              }}
            />
          );
        }}
      />
      {renderConfirmationPortal(
        isLoadingCancelMasterSchedule ||
          isLoadingUncancelMasterSchedule ||
          isLoadingLockMasterSchedule ||
          isLoadingUnlockMasterSchedule,
        isErrorCancelMasterSchedule ||
          isErrorUncancelMasterSchedule ||
          isErrorLockMasterSchedule ||
          isErrorUnlockMasterSchedule,
        isSuccessCancelMasterSchedule ||
          isSuccessUncancelMasterSchedule ||
          isSuccessLockMasterSchedule ||
          isSuccessUnlockMasterSchedule,
      )}
    </Scrollbar>
  );
};
